import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  documentId,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { careSourceMatchesScope } from './careSourceScope';
import {
  shouldRememberCareSourceActivity,
  shouldShowCareSourceInActivity,
  sortCareSourcesByNewest,
  getCareSourceSavedRecordCount,
  getCareSourceSavedTimelineCards,
  getCareSourceTimelineEntryIds,
  getCareSourceTimelinePointerCount,
  getCareSourceTimelinePointerEntryIds,
  getCareSourceTimelinePointerDateKeys,
  buildCareSourceTimelineDateLabels,
  hasCareSourceSavedTimelineRecords,
} from './careSourceStatusModel';
import {
  CARE_SOURCE_SESSION_ACTIVITY_EVENT,
  getRememberedCareSourceActivityIds,
  rememberCareSourceActivity,
} from './careSourceSessionActivity';

const SOURCE_NOTES_COLLECTION = 'careSources';
const TIMELINE_COLLECTION = 'dailyLogs';
const RECENT_SOURCE_LIMIT = 5;
const FIRESTORE_IN_QUERY_LIMIT = 10;

export const scopeCareSourcesToChild = (sources = [], childId = '') => (
  childId ? sources.filter((source) => source.childId === childId) : []
);

const chunkArray = (items = [], size = FIRESTORE_IN_QUERY_LIMIT) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const toTimelineDateKey = (entry = {}) => {
  const storedDateKey = entry.localDate || entry.entryDayKey;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(storedDateKey || ''))) {
    return storedDateKey;
  }

  const timestamp = entry.timestamp?.toDate?.()
    || entry.timestamp
    || entry.createdAt?.toDate?.()
    || entry.createdAt
    || null;
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const loadTimelineDatesBySource = async (sources = []) => {
  const plannerBackedDatesBySource = new Map();
  const legacySources = [];

  sources.forEach((source) => {
    const savedTimelineCards = getCareSourceSavedTimelineCards(source);
    const allCardsHaveDates = savedTimelineCards.length > 0
      && savedTimelineCards.every((card) => /^\d{4}-\d{2}-\d{2}$/.test(String(card.localDate || '')));
    const plannerDateKeys = [...new Set(savedTimelineCards
      .map((card) => card.localDate)
      .filter(Boolean))];
    const pointerDateKeys = getCareSourceTimelinePointerDateKeys(source);

    if (allCardsHaveDates) {
      plannerBackedDatesBySource.set(source.id, {
        dateKeys: plannerDateKeys,
        dateLabels: buildCareSourceTimelineDateLabels(plannerDateKeys),
      });
      return;
    }

    if (pointerDateKeys.length) {
      plannerBackedDatesBySource.set(source.id, {
        dateKeys: pointerDateKeys,
        dateLabels: buildCareSourceTimelineDateLabels(pointerDateKeys),
      });
      return;
    }

    legacySources.push(source);
  });

  const entryIds = [...new Set(
    legacySources.flatMap((source) => getCareSourceTimelineEntryIds(source))
  )].filter(Boolean);

  if (!entryIds.length) {
    return plannerBackedDatesBySource;
  }

  const dateKeysByEntryId = new Map();
  for (const ids of chunkArray(entryIds)) {
    const entriesQuery = query(
      collection(db, TIMELINE_COLLECTION),
      where(documentId(), 'in', ids)
    );
    const snapshot = await getDocs(entriesQuery);
    snapshot.docs.forEach((doc) => {
      const dateKey = toTimelineDateKey(doc.data());
      if (dateKey) {
        dateKeysByEntryId.set(doc.id, dateKey);
      }
    });
  }

  legacySources.forEach((source) => {
    const dateKeys = getCareSourceTimelineEntryIds(source)
      .map((entryId) => dateKeysByEntryId.get(entryId))
      .filter(Boolean);
    const dateLabels = buildCareSourceTimelineDateLabels(dateKeys);
    if (dateLabels.length || dateKeys.length) {
      plannerBackedDatesBySource.set(source.id, {
        dateKeys: [...new Set(dateKeys)],
        dateLabels,
      });
    }
  });

  return plannerBackedDatesBySource;
};

const attachTimelineDateLabels = async (sources = []) => {
  const sourcesWithSavedRecords = sources.filter(hasCareSourceSavedTimelineRecords);
  if (!sourcesWithSavedRecords.length) {
    return sources;
  }

  const timelineDatesBySource = await loadTimelineDatesBySource(sourcesWithSavedRecords);
  return sources.map((source) => ({
    ...source,
    savedTimelineDateKeys: timelineDatesBySource.get(source.id)?.dateKeys || [],
    savedTimelineDateLabels: timelineDatesBySource.get(source.id)?.dateLabels || [],
  }));
};

export const buildTimelineRefreshKey = (source = {}) => {
  if (!source.id) {
    return '';
  }

  const timelineEntryIds = getCareSourceTimelineEntryIds(source);
  const savedRecordCount = getCareSourceSavedRecordCount(source);
  const timelinePointerCount = getCareSourceTimelinePointerCount(source);
  const savedTimelineCards = getCareSourceSavedTimelineCards(source);
  const timelinePointerEntryIds = getCareSourceTimelinePointerEntryIds(source);
  const timelinePointerDateKeys = getCareSourceTimelinePointerDateKeys(source);
  const plannerIdentity = savedTimelineCards.length
    ? savedTimelineCards.map((card) => `${card.entryId}|${card.plannerCardKey}|${card.localDate}`).join(',')
    : timelineEntryIds.join(',');
  const pointerIdentity = [
    ...timelinePointerEntryIds,
    ...timelinePointerDateKeys,
    timelinePointerCount,
  ].join(',');

  return [
    source.childId || '',
    source.id || '',
    savedRecordCount,
    timelinePointerCount,
    plannerIdentity,
    pointerIdentity,
  ].join(':');
};

export const dispatchTimelineRefreshForSource = (source = {}) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
    detail: {
      childId: source.childId || null,
      sourceNoteId: source.id || null,
      collection: 'dailyLogs',
    },
  }));
};

export const useRecentCareSources = ({
  childId = '',
  sourceContext = '',
  documentType = '',
  sourceKind = '',
  sourceType = '',
  activityFilter = null,
  includeAllMatching = false,
  notifyTimelineChanges = true,
} = {}) => {
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);
  const [loadedChildId, setLoadedChildId] = useState(null);
  const [sessionActivityVersion, setSessionActivityVersion] = useState(0);
  const refreshedSourceKeysRef = useRef(new Set());
  const sourcesChildIdRef = useRef(childId);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return undefined;
    }

    const handleSessionActivityChange = () => {
      setSessionActivityVersion((current) => current + 1);
    };

    window.addEventListener(CARE_SOURCE_SESSION_ACTIVITY_EVENT, handleSessionActivityChange);
    return () => {
      window.removeEventListener(CARE_SOURCE_SESSION_ACTIVITY_EVENT, handleSessionActivityChange);
    };
  }, []);

  useEffect(() => {
    if (sourcesChildIdRef.current !== childId) {
      sourcesChildIdRef.current = childId;
      setSources([]);
      setError(null);
    }

    if (!childId) {
      setSources([]);
      setError(null);
      return undefined;
    }

    const sourceQuery = query(
      collection(db, SOURCE_NOTES_COLLECTION),
      where('childId', '==', childId)
    );

    let isActive = true;

    const unsubscribe = onSnapshot(
      sourceQuery,
      async (snapshot) => {
        const allSources = scopeCareSourcesToChild(snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((source) => careSourceMatchesScope(source, {
            sourceContext,
            documentType,
            sourceKind,
            sourceType,
          }))
          .filter((source) => source.status !== 'deleted'), childId);

        allSources
          .filter(shouldRememberCareSourceActivity)
          .forEach((source) => rememberCareSourceActivity(source.id));

        const rememberedSourceIds = getRememberedCareSourceActivityIds();
        const nextSources = includeAllMatching
          ? allSources
          : allSources.filter((source) => shouldShowCareSourceInActivity(source, { rememberedSourceIds }));
        const filteredSources = typeof activityFilter === 'function'
          ? nextSources.filter(activityFilter)
          : nextSources;

        if (notifyTimelineChanges) {
          filteredSources
            .filter(hasCareSourceSavedTimelineRecords)
            .forEach((source) => {
              const refreshKey = buildTimelineRefreshKey(source);
              if (!refreshKey || refreshedSourceKeysRef.current.has(refreshKey)) {
                return;
              }

              refreshedSourceKeysRef.current.add(refreshKey);
              dispatchTimelineRefreshForSource(source);
            });
        }

        try {
          const recentSources = includeAllMatching
            ? sortCareSourcesByNewest(filteredSources)
            : sortCareSourcesByNewest(filteredSources).slice(0, RECENT_SOURCE_LIMIT);
          const enrichedSources = await attachTimelineDateLabels(recentSources);
          if (!isActive) return;

          setLoadedChildId(childId);
          setSources(enrichedSources);
          setError(null);
        } catch (timelineDateError) {
          if (!isActive) return;

          setLoadedChildId(childId);
          setSources(
            includeAllMatching
              ? sortCareSourcesByNewest(filteredSources)
              : sortCareSourcesByNewest(filteredSources).slice(0, RECENT_SOURCE_LIMIT)
          );
          setError(null);
        }
      },
      (snapshotError) => {
        if (!isActive) return;
        setError(snapshotError);
        setLoadedChildId(childId);
      }
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [activityFilter, childId, documentType, includeAllMatching, notifyTimelineChanges, sessionActivityVersion, sourceContext, sourceKind, sourceType]);

  const reload = useCallback(() => {
    setError(null);
    setLoadedChildId(null);
    setSessionActivityVersion(version => version + 1);
  }, []);
  const scopedSources = scopeCareSourcesToChild(sources, childId);

  return {
    error,
    loading: Boolean(childId) && loadedChildId !== childId,
    reload,
    sources: scopedSources,
  };
};

export default useRecentCareSources;
